/**
 * President (Scum / Daifugō) — server-authoritative rules engine, 4 players, one deal per match.
 *
 * Rules implemented:
 *  - One 52-card deck dealt out, 13 each. Twos are the highest cards, then ace down to three. Suits do not matter.
 *  - The holder of the three of clubs leads the first pile. A leader plays one card or a set of equal-rank cards
 *    (a pair, three or four of a kind).
 *  - Each next player must either play the same number of cards of a strictly higher rank, or pass. Having passed,
 *    a player sits out until the pile is cleared.
 *  - A pile is cleared when a two is played (the player leads again) or when everybody else has passed (the last
 *    player to play leads). If that player has gone out, the next player with cards leads.
 *  - A player who plays their last card is out. They finish in the order they went out: President, Vice President,
 *    Neutral, and Scum (the player left holding cards). The game ends when one player is left.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface PresidentConfig {
  clearDelayMs: number;
}

export type PresidentPhase = 'PLAYING' | 'CLEAR' | 'MATCH_END';

export interface PresidentPlay {
  seat: number;
  cards: CardId[];
}

export interface PresidentState {
  config: PresidentConfig;
  players: string[];
  phase: PresidentPhase;
  hands: CardId[][];
  /** Plays on the pile in order; the last one is the one to beat. */
  pile: PresidentPlay[];
  /** Seats that have passed since the pile was started. */
  passed: boolean[];
  turn: number;
  /** Seat that made the last play on the pile. */
  lastPlayer: number;
  /** Seat that leads the next pile once the current one is cleared. */
  nextLeader: number;
  /** Why the last pile was cleared. */
  clearedBy: 'two' | 'passes' | null;
  /** The pile that was just cleared, kept for the table to show while it is swept away. */
  lastPile: PresidentPlay[];
  pileNo: number;
  plays: number;
  /** Seats in the order they went out. */
  out: number[];
}

export type PresidentAction = { type: 'play'; cards: CardId[] } | { type: 'pass' };

export interface PresidentLegal {
  /** Every card set the seat may play now. */
  sets: CardId[][];
  canPass: boolean;
}

export interface PresidentView {
  gameType: 'PRESIDENT';
  config: PresidentConfig;
  seat: number | null;
  phase: PresidentPhase;
  hand: CardId[];
  handCounts: number[];
  pile: PresidentPlay[];
  passed: boolean[];
  turn: number;
  lastPlayer: number;
  clearedBy: 'two' | 'passes' | null;
  pileNo: number;
  out: number[];
  /** The number of cards a follower must play (0 when leading). */
  need: number;
  totals: number[];
}

const DEFAULTS: PresidentConfig = { clearDelayMs: 1400 };
const SEATS = 4;

/** Strength of a card: twos high, then ace down to three. */
export const presidentPower = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 2 ? 15 : r;
};

/** Ranks present in a hand, grouped. */
function groups(hand: readonly CardId[]): Map<number, CardId[]> {
  const g = new Map<number, CardId[]>();
  for (const c of hand) {
    const p = presidentPower(c);
    g.set(p, [...(g.get(p) ?? []), c]);
  }
  return g;
}

function combinations(cards: readonly CardId[], n: number): CardId[][] {
  if (n === 0) return [[]];
  const out: CardId[][] = [];
  cards.forEach((c, i) => {
    for (const rest of combinations(cards.slice(i + 1), n - 1)) out.push([c, ...rest]);
  });
  return out;
}

/** The play a follower has to beat: card count and rank. */
export function presidentTarget(pile: readonly PresidentPlay[]): { need: number; power: number } {
  const top = pile.at(-1);
  return top ? { need: top.cards.length, power: presidentPower(top.cards[0]!) } : { need: 0, power: 0 };
}

/** Every set of cards a seat may play, and whether it may pass. */
export function presidentLegal(hand: readonly CardId[], pile: readonly PresidentPlay[]): PresidentLegal {
  const { need, power } = presidentTarget(pile);
  const sets: CardId[][] = [];
  for (const [p, cards] of groups(hand)) {
    if (need === 0) {
      for (let n = 1; n <= cards.length; n++) sets.push(...combinations(cards, n));
    } else if (p > power && cards.length >= need) {
      sets.push(...combinations(cards, need));
    }
  }
  return { sets, canPass: need > 0 };
}

const sameSet = (a: readonly CardId[], b: readonly CardId[]) => a.length === b.length && a.every((c) => b.includes(c));

/** Seats still holding cards, clockwise from `from` (inclusive). */
function nextWithCards(hands: readonly (readonly CardId[])[], from: number): number {
  for (let i = 0; i < SEATS; i++) {
    const seat = (from + i) % SEATS;
    if (hands[seat]!.length > 0) return seat;
  }
  return from;
}

function finish(state: PresidentState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  const left = state.hands.map((h, s) => (h.length > 0 ? s : -1)).filter((s) => s >= 0);
  for (const s of left) if (!state.out.includes(s)) state.out.push(s);
  events.push({ type: 'matchEnd', standings: standings(state) });
}

function clearPile(state: PresidentState, by: 'two' | 'passes', leader: number, events: EngineEvent[]): void {
  state.phase = 'CLEAR';
  state.clearedBy = by;
  state.lastPile = state.pile.map((p) => ({ seat: p.seat, cards: [...p.cards] }));
  state.nextLeader = leader;
  state.turn = leader;
  events.push({ type: 'pileCleared', by, leader });
}

export const presidentEngine: GameEngine<PresidentConfig, PresidentState, PresidentAction, PresidentView, PresidentLegal> = {
  gameType: 'PRESIDENT',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('clearDelayMs', c.clearDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'President needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    const leader = Math.max(0, hands.findIndex((h) => h.includes('3C')));
    return {
      config,
      players,
      phase: 'PLAYING',
      hands: hands.map((h) => sortHand(h)),
      pile: [],
      passed: [false, false, false, false],
      turn: leader,
      lastPlayer: leader,
      nextLeader: leader,
      clearedBy: null,
      lastPile: [],
      pileNo: 1,
      plays: 0,
      out: [],
    };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'play' && Array.isArray(raw.cards) && raw.cards.length >= 1 && raw.cards.length <= 4 && raw.cards.every((c) => typeof c === 'string' && c.length <= 4)) {
      return { type: 'play', cards: raw.cards as string[] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown President action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(state.phase === 'PLAYING' && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    const legal = presidentLegal(hand, state.pile);

    if (action.type === 'pass') {
      assertEngine(legal.canPass, 'ILLEGAL_MOVE', 'You must lead a card');
      state.passed[seat] = true;
      events.push({ type: 'pass', seat });
    } else {
      assertEngine(new Set(action.cards).size === action.cards.length, 'INVALID_CARD', 'Duplicate card');
      assertEngine(action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Card not in hand');
      assertEngine(legal.sets.some((s) => sameSet(s, action.cards)), 'ILLEGAL_MOVE', 'That play is not allowed now');
      removeCards(hand, action.cards);
      state.pile.push({ seat, cards: [...action.cards] });
      state.lastPlayer = seat;
      state.plays++;
      events.push({ type: 'cardsPlayed', seat, cards: [...action.cards] });
      if (hand.length === 0) {
        state.out.push(seat);
        events.push({ type: 'out', seat, place: state.out.length });
      }
    }

    if (state.hands.filter((h) => h.length > 0).length <= 1) {
      finish(state, events);
      return events;
    }

    const playedTwo = action.type === 'play' && presidentPower(action.cards[0]!) === 15;
    if (playedTwo) {
      clearPile(state, 'two', nextWithCards(state.hands, seat), events);
      return events;
    }
    // Anyone besides the last player who still holds cards and has not passed may answer.
    for (let i = 1; i < SEATS; i++) {
      const s = (seat + i) % SEATS;
      if (s !== state.lastPlayer && state.hands[s]!.length > 0 && !state.passed[s]) {
        state.turn = s;
        return events;
      }
    }
    clearPile(state, 'passes', nextWithCards(state.hands, state.lastPlayer), events);
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { sets: [], canPass: false };
    return presidentLegal(state.hands[seat]!, state.pile);
  },

  getTimeoutAction(state, seat) {
    const legal = presidentLegal(state.hands[seat]!, state.pile);
    if (legal.canPass) return { type: 'pass' };
    const lowest = [...legal.sets].sort((a, b) => a.length - b.length || presidentPower(a[0]!) - presidentPower(b[0]!))[0]!;
    return { type: 'play', cards: lowest };
  },

  getView(state, seat) {
    const showing = state.phase === 'CLEAR' ? state.lastPile : state.pile;
    return {
      gameType: 'PRESIDENT',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      pile: showing.map((p) => ({ seat: p.seat, cards: [...p.cards] })),
      passed: [...state.passed],
      turn: state.turn,
      lastPlayer: state.lastPlayer,
      clearedBy: state.clearedBy,
      pileNo: state.pileNo,
      out: [...state.out],
      need: presidentTarget(state.pile).need,
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep(state) {
    return state.phase === 'CLEAR' ? { kind: 'clearPile', delayMs: state.config.clearDelayMs } : null;
  },

  runSystemStep(state) {
    const events: EngineEvent[] = [];
    if (state.phase !== 'CLEAR') return events;
    state.pile = [];
    state.passed = [false, false, false, false];
    state.phase = 'PLAYING';
    state.turn = state.nextLeader;
    state.lastPlayer = state.nextLeader;
    state.pileNo++;
    events.push({ type: 'pileStarted', leader: state.turn });
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.pileNo,
};

/** Places in the order players went out, then whoever is left (Scum) last. */
function standings(state: PresidentState): Standing[] {
  const rest = [0, 1, 2, 3].filter((s) => !state.out.includes(s));
  return [...state.out, ...rest].map((seat, i) => ({ seat, place: i + 1, score: 100 - i * 10 - state.hands[seat]!.length }));
}
